// Part card image — the "lead visual" for a part on the Parts list and the
// Quote builder rows. Picks the right thing to render in this priority order:
//
//   1. part.thumbnailUrl set                      → <img> (explicit override)
//   2. First attachedDrawings entry is a PDF URL  → PdfThumbnail (page 1)
//   3. First entry is an image URL                → <img>
//   4. First entry is a CAD-ish URL               → labelled placeholder card
//   5. Anything else / nothing                    → PartGraphic SVG fallback
//
// The thumbnailUrl override lets Justin attach an externally-generated
// thumbnail (e.g. STEP-render → PNG) when the canonical drawing isn't a
// previewable format. Set via the ★ button in PartDrawingThumbnails.
//
// Heuristics are the same brand the rest of the v2 codebase uses (see
// PartDrawingThumbnails): a "URL" here is anything that parses cleanly as
// http(s)/data/blob — bare document IDs ("doc_abc123") aren't fetchable on
// their own so we skip straight to the SVG fallback for those.
//
// The PdfViewerModal lives INSIDE this component (state owned here), but only
// opens when the parent passes no onClick — when the card itself is clickable
// (Parts list → /parts/:id), we don't intercept; the page handles navigation.

import { useMemo, useState } from 'react';
import type { Part } from '@/shared/crmTypes';
import { PdfThumbnail } from './PdfThumbnail';
import { PdfViewerModal } from './PdfViewerModal';
import { PartGraphic } from './PartGraphic';

export interface PartCardImageProps {
  part: Part;
  /** Pixel width budget — card aspect ratio comes from the source. Default 240. */
  width?: number;
  /** Optional click handler (e.g. open PdfViewerModal, navigate). When provided,
   *  takes precedence over the built-in PDF viewer. */
  onClick?: () => void;
  /** Optional className for outer wrapper. */
  className?: string;
  /** When true, the built-in PDF viewer is disabled even if no onClick is
   *  passed and there's a PDF source. Use this when this component is nested
   *  inside an interactive element (button/link) — having an interactive
   *  child would break keyboard nav and produce invalid HTML. */
  staticVisual?: boolean;
}

// Same URL-shape gate as PartDrawingThumbnails — anything not parseable as
// http(s)/data/blob is treated as a non-fetchable document ID.
function isUrlish(entry: string): boolean {
  return /^(https?:|data:|blob:)/i.test(entry);
}

function stripQueryHash(entry: string): string {
  return entry.split(/[?#]/)[0];
}

function isPdfUrl(entry: string): boolean {
  if (!isUrlish(entry)) return false;
  return /\.pdf$/i.test(stripQueryHash(entry));
}

function isImageUrl(entry: string): boolean {
  if (!isUrlish(entry)) return false;
  return /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(stripQueryHash(entry));
}

type CadKind = 'dxf' | 'step' | 'iges';

function detectCadKind(entry: string): CadKind | null {
  // We accept either a URL or a doc-ID-with-extension here — operators upload
  // STEP/DXF files all the time and even when the URL hasn't been wired up
  // yet, the filename usually still has the extension. So check both.
  const base = isUrlish(entry) ? stripQueryHash(entry) : entry;
  if (/\.dxf$/i.test(base)) return 'dxf';
  if (/\.(step|stp)$/i.test(base)) return 'step';
  if (/\.(iges|igs)$/i.test(base)) return 'iges';
  return null;
}

// Filename-ish display label — last path segment, decoded, length-capped.
function shortName(entry: string): string {
  try {
    const u = new URL(entry);
    const last = u.pathname.split('/').filter(Boolean).pop();
    if (last) return decodeURIComponent(last);
  } catch {
    /* not a URL — fall through */
  }
  const parts = entry.split(/[/\\]/);
  const tail = parts[parts.length - 1] || entry;
  return tail.length > 40 ? `${tail.slice(0, 37)}…` : tail;
}

const CAD_META: Record<CadKind, { glyph: string; label: string }> = {
  dxf: { glyph: '📐', label: 'DXF' },
  step: { glyph: '🧊', label: 'STEP' },
  iges: { glyph: '🧊', label: 'IGES' },
};

export function PartCardImage({
  part,
  width = 240,
  onClick,
  className,
  staticVisual = false,
}: PartCardImageProps) {
  const first = useMemo(() => part.attachedDrawings?.[0], [part.attachedDrawings]);
  const [viewerOpen, setViewerOpen] = useState(false);

  const wrapperClass = [
    'bg-paper border border-line rounded overflow-hidden',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  // Pick the visual. We do this before the wrapper so the component just
  // returns one shape — keeps the inner-vs-outer click contract straight.
  //
  // pdfSource tracks the underlying SOURCE drawing (not the thumbnail) so a
  // click opens the real PDF in the viewer. When part.thumbnailUrl is set
  // but the first attached drawing is still a PDF, we render the explicit
  // thumb here but the click still opens the source PDF.
  let inner: React.ReactNode;
  let pdfSource: string | null = null;
  if (first && isPdfUrl(first)) pdfSource = first;

  if (part.thumbnailUrl) {
    inner = (
      <img
        src={part.thumbnailUrl}
        alt={`Drawing for ${part.number}`}
        width={width}
        className="block object-contain w-full h-auto"
        style={{ maxWidth: width }}
      />
    );
  } else if (first && isPdfUrl(first)) {
    inner = (
      <PdfThumbnail
        source={first}
        width={width}
        // No onClick here — the wrapper handles it so the click area covers
        // the full card, not just the canvas pixels.
        className="block"
      />
    );
  } else if (first && isImageUrl(first)) {
    inner = (
      <img
        src={first}
        alt={`Drawing for ${part.number}`}
        width={width}
        className="block object-contain w-full h-auto"
        style={{ maxWidth: width }}
      />
    );
  } else if (first && detectCadKind(first)) {
    const kind = detectCadKind(first)!;
    const meta = CAD_META[kind];
    const label = shortName(first);
    // Use an aspect-ratio close to a sheet drawing so it doesn't look weird
    // sitting next to PdfThumbnail siblings in a grid.
    const heightPx = Math.round(width * (11 / 8.5));
    inner = (
      <div
        className="flex flex-col items-center justify-center bg-bg text-center px-3"
        style={{ width, height: heightPx }}
      >
        <div className="text-5xl mb-2" aria-hidden>
          {meta.glyph}
        </div>
        <div className="text-xs font-semibold text-navy tracking-wide">{meta.label}</div>
        <div
          className="text-[10px] text-text-mute mt-1 break-all line-clamp-2"
          title={label}
        >
          {label}
        </div>
        <div className="text-[10px] text-text-mute italic mt-2">
          preview coming soon
        </div>
      </div>
    );
  } else {
    // Nothing usable — fall back to the per-type diagrammatic SVG.
    inner = <PartGraphic part={part} width={width} />;
  }

  // Click contract:
  //   - parent passed onClick → use it (e.g. Parts list navigates to detail)
  //   - else if we have a PDF source AND staticVisual is false
  //     → open the modal in-place
  //   - else → no-op, image is purely decorative (also the staticVisual case
  //     when nested inside a parent interactive element)
  const handleClick = onClick
    ? onClick
    : pdfSource && !staticVisual
      ? () => setViewerOpen(true)
      : undefined;

  const interactive = typeof handleClick === 'function';

  return (
    <>
      <div
        className={wrapperClass}
        onClick={handleClick}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onKeyDown={
          interactive
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick?.();
                }
              }
            : undefined
        }
        aria-label={`Drawing for part ${part.number}`}
      >
        {inner}
      </div>

      {pdfSource && !onClick && !staticVisual && (
        <PdfViewerModal
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          source={pdfSource}
          title={`${part.number} — ${shortName(pdfSource)}`}
          downloadFilename={shortName(pdfSource)}
        />
      )}
    </>
  );
}
